
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { Query_RootContractor_Staff_NoteArgs, User, Contractor_Staff_Note } from "@/generated/graphql";
import GET_CONTRACTORS_STAFF_NOTES from "@/apollo/contractors/GetContractorStaffNotes.gql";
import NotesCard from "@/components/projects/activities/NotesCard.vue";
import NotesCollectionFilters from "@/components/projects/activities/NotesCollectionFilters.vue";
import { cloneDeep } from "lodash";
import EmptyListImageManagers from "@/components/icons/emptyListImages/EmptyListImageManagers.vue";
import moment from "moment";
import { isEqual } from "lodash";
import { getFiltersDataFromQuery, joinQueryValues } from "@/helpers/router/prepareFiltersQuery";
import PaginationConfig from "@/entities/PaginationConfig";
import paginationConfig from "@/data/paginationConfig";
import CardPagination from "@/components/card/PaginationNew.vue";
import { getPaginationDataFromQuery, getPaginationQuery } from "@/helpers/router/preparePaginationQuery";
import getPaginationOffset from "@/helpers/pagination/getPaginationOffset";
import { getEmptyFiltersResultMessage, getEmptyListMessage } from "@/helpers/lists/emptyListMessagesNew";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";

interface NotesCollectionFiltersQuery {
    nameJobTitle: string
    createdAt: string
}

const notesCollectionFiltersTemplate = {
    nameJobTitle: "",
    createdAt: ""
}

@Component({
    components: {
        NotesCard,
        EmptyListImageManagers,
        NotesCollectionFilters,
        CardPagination,
        EmptyListPlaceholder
    },
    apollo: {
        notes: {
            query: GET_CONTRACTORS_STAFF_NOTES,
            variables() {
                return this.noteFilter;
            },
            update(data) {
                this.paginationConfig.total = data.total.aggregate.count;
                this.paginationConfig.loadedCount = data.notes.length;
                return data.notes;
            },
        },
    },

})

export default class Notes extends Vue {
    @Prop({ type: Object, required: true }) readonly currentUser!: User;
    @Prop({ type: String, required: true }) readonly companyId!: string;

    notes: Contractor_Staff_Note[] = []
    paginationConfig: PaginationConfig = { ...paginationConfig };
    directoryFilters: NotesCollectionFiltersQuery = cloneDeep(
        notesCollectionFiltersTemplate
    );
    private isUpdatingFromUserInput = false;
    private queryUpdateTimer: any = null;

    get emptyListMessage(): string {
        const entitiesName = "notes";
        const filtersEmpty = isEqual(
            this.directoryFilters,
            notesCollectionFiltersTemplate
        );

        if (filtersEmpty) {
            return getEmptyListMessage(entitiesName);
        }

        return getEmptyFiltersResultMessage(entitiesName);
    }

    get empty(): boolean {
        if (!this.notes) {
            return false;
        }
        return this.notes?.length === 0;
    }

    get isLoading(): boolean {
        return this.$apollo.queries.notes?.loading;
    }

    get noteFilter(): Query_RootContractor_Staff_NoteArgs {
        const { page, limit } = this.paginationConfig;
        // const nextOffset = page * limit - limit;
        const { createdAt, nameJobTitle } = this.directoryFilters;
        const filterConditions = [];

        if (nameJobTitle) {
            filterConditions.push({
                _or: [
                    {
                        contractor: { user: { given_name: { _ilike: `%${nameJobTitle}%` } } },
                    },
                    {
                        contractor: { user: { family_name: { _ilike: `%${nameJobTitle}%` } } },
                    },
                    {
                        contractor: { user: { profile: { job_title: { _ilike: `%${nameJobTitle}%` } } } },
                    },
                    {
                        contractor: { user: { user_profile_by_managements: { job_title: { _ilike: `%${nameJobTitle}%` } } } },
                    },
                ]
            });
        }

         if (createdAt) {
      const startDate = moment(createdAt).utc().startOf("day").toISOString();
      const endDate = moment(createdAt).utc().endOf("day").toISOString();

      const datesConditions = [
        { created_at: { _gte: startDate } },
        { created_at: { _lte: endDate } },
      ];

      filterConditions.push({
        _and: datesConditions,
      });
    }

        return {
            offset: getPaginationOffset({ page, limit }),
            limit: limit,
            where: {
                _and: filterConditions,
                project_id: {
                    _eq: this.$route.params.id,
                },
            },
        };

    }

    get queryPropsToWatch(): string {
        return JSON.stringify([this.directoryFilters, this.paginationConfig]);
    }

    get queryParams(): {
        filters: NotesCollectionFiltersQuery;
        pagination: PaginationConfig;
    } {
        const query = this.$route.query;

        const {
            created_at: createdAt,
            name_job_title: nameJobTitle
        } = query;

        const queryData = {
            createdAt,
            nameJobTitle,
        };

        const filters = getFiltersDataFromQuery<NotesCollectionFiltersQuery>(
            notesCollectionFiltersTemplate,
            queryData
        );
        const pagination = {
            ...this.paginationConfig,
            ...getPaginationDataFromQuery(query),
        };

        return {
            filters,
            pagination,
        };
    }

    get isChosenPageEmpty(): boolean {
        const collectionEmpty = this.notes?.length === 0;
        const notFirstPage = this.paginationConfig.page !== 1;

        return !this.isLoading && collectionEmpty && notFirstPage;
    }    @Watch("queryPropsToWatch")
    updateQuery(): void {
        if (this.isChosenPageEmpty) {
            return this.showFirstPage();
        }
        
        // Clear any existing timer
        clearTimeout(this.queryUpdateTimer);
        
        // Set a new timer to delay the URL update
        this.queryUpdateTimer = setTimeout(() => {
            const paginationQuery = getPaginationQuery(this.paginationConfig);

            const { createdAt, ...otherFilters } = this.directoryFilters;
            
            // Only include nameJobTitle in query if it's not empty
            const filteredOtherFilters = Object.entries(otherFilters).reduce((acc, [key, value]) => {
                if (value !== "") {
                    acc[key] = value;
                }
                return acc;
            }, {});

            const query = joinQueryValues({
                ...paginationQuery,
                ...filteredOtherFilters,
                ...(createdAt ? { created_at: createdAt } : {})
            });

            if (isEqual(query, this.$route.query)) {
                return;
            }
            
            this.isUpdatingFromUserInput = true;
            this.$router.push({ query }).catch(() => { });
            
            // Reset the flag after a short delay
            setTimeout(() => {
                this.isUpdatingFromUserInput = false;
            }, 100);
        }, 800); // Debounce URL updates by 800ms
    }    @Watch("$route.query", { immediate: true })
    onQueryChange(): void {
        // Skip updates if we're the ones who triggered the change
        if (this.isUpdatingFromUserInput) {
            return;
        }
        
        const { filters, pagination } = this.queryParams;
        
        // Only update the filters and pagination if they've actually changed
        this.$nextTick(() => {
            const filtersChanged = !isEqual(this.directoryFilters, filters);
            const paginationChanged = !isEqual(this.paginationConfig, pagination);
            
            if (filtersChanged) {
                this.$set(this, 'directoryFilters', filters);
            }
            
            if (paginationChanged) {
                this.paginationConfig = pagination;
            }
        });
    }

    showFirstPage(): void {
        this.paginationConfig = {
            ...this.paginationConfig,
            page: paginationConfig.page,
        };
    }


    resetFilters(): void {
        if (isEqual(this.directoryFilters, notesCollectionFiltersTemplate)) {
            return;
        }

        this.directoryFilters = cloneDeep(notesCollectionFiltersTemplate);
        this.paginationConfig = { ...paginationConfig };
    }    updateFilters(updatedFilters: NotesCollectionFiltersQuery): void {
        const dataEqual = isEqual(this.directoryFilters, updatedFilters);
        if (dataEqual) {
            return;
        }
        
        this.isUpdatingFromUserInput = true;
        
        // Only update the specific properties that have changed
        const newFilters = { ...this.directoryFilters };
        
        if (updatedFilters.nameJobTitle !== undefined) {
            newFilters.nameJobTitle = updatedFilters.nameJobTitle;
        }
        
        if (updatedFilters.createdAt !== undefined) {
            newFilters.createdAt = updatedFilters.createdAt;
        }
        
        this.$set(this, 'directoryFilters', newFilters);
        
        // Reset pagination to first page when filters change
        if (this.paginationConfig.page !== 1) {
            this.paginationConfig = { ...this.paginationConfig, page: 1 };
        }
    }

}

